import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MessageService } from 'primeng/api';
import {
    ConfiguratorDatastoreService,
    ConfiguratorDataViewService,
    DatastoreDto,
    DataViewLiteDto,
    SaveQuery
} from '@tj/tj-dmp-lib-api';
import { FilterPresetService } from '../filter/filter-preset/filter-preset.service';
import { PresetCompatibilityService, CompatibilityResult } from '../../services/preset-compatibility.service';
import { forkJoin } from 'rxjs';

export interface CopyToTenantRequest {
    targetDatastore: string;
    targetView: string;
    targetPartner: string;
    newName: string;
}

@Component({
    selector: 'dmp-copy-preset-to-tenant',
    templateUrl: './copy-preset-to-tenant.component.html',
    styleUrls: ['./copy-preset-to-tenant.component.scss'],
    standalone: false
})
export class CopyPresetToTenantComponent implements OnInit {
    @Input() visible = false;
    @Input() currentPreset: SaveQuery;
    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() onCopySuccess = new EventEmitter<SaveQuery>();

    datastores: DatastoreDto[] = [];
    selectedDatastore: DatastoreDto = null;

    views: DataViewLiteDto[] = [];
    selectedView: DataViewLiteDto = null;

    newPresetName = '';

    isValidating = false;
    isCompatible = false;
    compatibilityResult: CompatibilityResult = null;

    isCopying = false;

    constructor(
        private datastoreService: ConfiguratorDatastoreService,
        private dataViewService: ConfiguratorDataViewService,
        private presetService: FilterPresetService,
        private compatibilityService: PresetCompatibilityService,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.loadDatastores();
    }

    loadDatastores(): void {
        this.datastoreService.getAllDatastoresUsingGET().subscribe(
            (datastores) => {
                this.datastores = datastores.filter(ds => ds.code !== this.currentPreset?.datastore);
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erreur',
                    detail: 'Impossible de charger la liste des tenants'
                });
            }
        );
    }

    onDatastoreChange(): void {
        this.selectedView = null;
        this.views = [];
        this.compatibilityResult = null;
        this.isCompatible = false;

        if (this.selectedDatastore?.code) {
            this.loadViews(this.selectedDatastore.code);
        }
    }

    loadViews(datastoreCode: string): void {
        this.dataViewService.getAllDataViewsUsingGET(datastoreCode).subscribe(
            (views) => {
                this.views = views;
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erreur',
                    detail: 'Impossible de charger les vues du tenant sélectionné'
                });
            }
        );
    }

    onViewChange(): void {
        this.compatibilityResult = null;
        this.isCompatible = false;

        if (this.selectedView?.code && this.selectedDatastore?.code) {
            this.validateCompatibility();
        }
    }

    validateCompatibility(): void {
        if (!this.currentPreset || !this.selectedDatastore || !this.selectedView) {
            return;
        }

        this.isValidating = true;
        this.compatibilityService.validatePresetCompatibility(
            this.currentPreset,
            this.selectedDatastore.code,
            this.selectedView.code
        ).subscribe(
            (result) => {
                this.compatibilityResult = result;
                this.isCompatible = result.compatible;
                this.isValidating = false;

                if (!result.compatible) {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Incompatibilité détectée',
                        detail: 'Le preset n\'est pas compatible à 100% avec la vue de destination. La copie est bloquée.'
                    });
                } else {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Compatible',
                        detail: 'Le preset est compatible avec la vue de destination'
                    });
                }
            },
            (error) => {
                this.isValidating = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erreur de validation',
                    detail: 'Impossible de valider la compatibilité du preset'
                });
            }
        );
    }

    canCopy(): boolean {
        return this.isCompatible &&
               !!this.selectedDatastore &&
               !!this.selectedView &&
               this.newPresetName.trim().length > 2;
    }

    copy(): void {
        if (!this.canCopy()) {
            return;
        }

        this.isCopying = true;

        // Conserver le même type de partner que le preset source :
        // - Si le preset source a un partner, on le copie (preset spécifique à un partner)
        // - Si le preset source n'a pas de partner (global), on le garde global ('' ou null)
        const targetPartner = this.currentPreset.partner || '';

        this.presetService.copyToTenant(
            this.currentPreset.id,
            this.selectedDatastore.code,
            this.selectedView.code,
            targetPartner,
            this.newPresetName.trim()
        ).subscribe(
            (newPreset) => {
                this.isCopying = false;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Preset copié',
                    detail: `Le preset "${this.newPresetName}" a été créé dans le tenant "${this.selectedDatastore.name}"`
                });
                this.onCopySuccess.emit(newPreset);
                this.close();
            },
            (error) => {
                this.isCopying = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erreur de copie',
                    detail: 'Impossible de copier le preset vers le tenant de destination'
                });
            }
        );
    }

    close(): void {
        this.visible = false;
        this.visibleChange.emit(false);
        this.reset();
    }

    reset(): void {
        this.selectedDatastore = null;
        this.selectedView = null;
        this.views = [];
        this.newPresetName = '';
        this.compatibilityResult = null;
        this.isCompatible = false;
        this.isValidating = false;
        this.isCopying = false;
    }

    getIssueIcon(issueType: string): string {
        switch (issueType) {
            case 'missing_column':
                return 'fas fa-columns';
            case 'missing_field':
                return 'fas fa-filter';
            case 'missing_table':
                return 'fas fa-table';
            case 'incompatible_constraint':
                return 'fas fa-exclamation-triangle';
            default:
                return 'fas fa-info-circle';
        }
    }
}
